/**
 * Custom Cards Section JavaScript
 * Handles slideshow functionality, navigation, and video interactions
 */

class CustomCardsSlideshow {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = sectionElement.dataset.sectionId;
    this.wrapper = sectionElement.querySelector(`#cards-wrapper-${this.sectionId}`);
    this.cards = sectionElement.querySelectorAll('.custom-card');
    this.navPrev = sectionElement.querySelector('.nav-prev');
    this.navNext = sectionElement.querySelector('.nav-next');
    this.dots = sectionElement.querySelectorAll('.dot');
    this.currentSlide = 0;
    this.totalSlides = this.cards.length;
    
    // Autoplay settings
    this.autoplayEnabled = sectionElement.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(sectionElement.dataset.autoplaySpeed) || 5000;
    this.autoplayInterval = null;
    
    this.init();
  }
  
  init() {
    this.setupVideoPlaceholders();
    if (this.totalSlides <= 1) {
      return; // No slideshow needed for single card
    }
    
    this.bindEvents();
    this.updateSlideshow();
    
    // Initialize autoplay if enabled
    if (this.autoplayEnabled) {
      this.startAutoPlay();
      this.setupAutoPlayPause();
    }
    
    // Handle keyboard navigation
    this.section.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.nextSlide();
      }
    });
    
    // Handle touch/swipe gestures
    this.setupTouchEvents();
  }
  
  bindEvents() {
    // Navigation arrows
    if (this.navPrev) {
      this.navPrev.addEventListener('click', () => this.prevSlide());
    }
    
    if (this.navNext) {
      this.navNext.addEventListener('click', () => this.nextSlide());
    }
    
    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });
  }
  
  setupVideoPlaceholders() {
    const videoPlaceholders = this.section.querySelectorAll('.video-placeholder');
    
    videoPlaceholders.forEach(placeholder => {
      placeholder.addEventListener('click', () => {
        const videoContainer = placeholder.closest('.video-container');
        const iframe = videoContainer.querySelector('.video-iframe');
        
        if (iframe) {
          // Show iframe and hide placeholder
          placeholder.style.display = 'none';
          iframe.style.display = 'block';
          
          // Start video playback by updating src with autoplay
          const currentSrc = iframe.src;
          if (currentSrc.includes('youtube.com')) {
            iframe.src = currentSrc.includes('autoplay=1') 
              ? currentSrc 
              : currentSrc + (currentSrc.includes('?') ? '&' : '?') + 'autoplay=1&mute=1';
          } else if (currentSrc.includes('vimeo.com')) {
            iframe.src = currentSrc.includes('autoplay=1') 
              ? currentSrc 
              : currentSrc + (currentSrc.includes('?') ? '&' : '?') + 'autoplay=1&muted=1';
          }
        }
      });
    });
    
    // Setup Shopify video controls
    const shopifyVideos = this.section.querySelectorAll('.shopify-video');
    shopifyVideos.forEach(video => {
      // Handle video loading
      video.addEventListener('loadedmetadata', () => {
        // Video is ready to play
        console.log('Shopify video loaded:', video);
      });
      
      // Handle video errors
      video.addEventListener('error', (e) => {
        console.error('Shopify video error:', e);
      });
      
      // Pause video when it's not in the active slide
      video.addEventListener('play', () => {
        const card = video.closest('.custom-card');
        const cardIndex = Array.from(this.cards).indexOf(card);
        if (cardIndex !== this.currentSlide) {
          video.pause();
        }
      });
    });
  }
  
  setupTouchEvents() {
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;
    
    this.wrapper.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    
    this.wrapper.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      endY = e.changedTouches[0].clientY;
      
      const deltaX = startX - endX;
      const deltaY = startY - endY;
      
      // Only trigger swipe if horizontal movement is greater than vertical
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }
    }, { passive: true });
  }
  
  nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
    this.updateSlideshow();
    this.resetAutoPlay(); // Reset autoplay timer on manual interaction
  }
  
  prevSlide() {
    this.currentSlide = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.updateSlideshow();
    this.resetAutoPlay(); // Reset autoplay timer on manual interaction
  }
  
  goToSlide(index) {
    this.currentSlide = index;
    this.updateSlideshow();
    this.resetAutoPlay();
  }
  
  updateSlideshow() {
  const translateX = -this.currentSlide * 100;
  this.wrapper.style.transform = `translateX(${translateX}%)`;

  this.dots.forEach((dot, index) => {
    dot.classList.toggle('active', index === this.currentSlide);
  });

  this.cards.forEach((card, index) => {
    card.setAttribute('aria-hidden', index !== this.currentSlide);
  });

  this.cards.forEach((card, index) => {
    card.classList.toggle('active', index === this.currentSlide);
  });

  this.pauseInactiveVideos();

  const activeCard = this.cards[this.currentSlide];
  const autoplayEnabled = activeCard?.dataset.videoAutoplay === 'true';
  const showControls = activeCard?.dataset.videoControls === 'true';

  if (autoplayEnabled) {
    const iframe = activeCard.querySelector('.video-iframe');
    const shopifyVideo = activeCard.querySelector('.shopify-video');

    if (iframe) {
      const baseSrc = iframe.src.split('?')[0];
      const newSrc = `${baseSrc}?autoplay=1&mute=1&rel=0&showinfo=0&controls=${showControls ? '1' : '0'}`;
      iframe.src = newSrc;
      iframe.style.display = 'block';

      const placeholder = activeCard.querySelector('.video-placeholder');
      if (placeholder) placeholder.style.display = 'none';
    }

    if (shopifyVideo) {
      shopifyVideo.controls = showControls;
      shopifyVideo.muted = true;
      shopifyVideo.play().catch(err => {
        console.warn('Autoplay falhou:', err);
      });
    }
  }
  }   
  
  pauseInactiveVideos() {
    this.cards.forEach((card, index) => {
      if (index !== this.currentSlide) {
        // Handle external videos (YouTube/Vimeo)
        const iframes = card.querySelectorAll('.video-iframe');
        iframes.forEach(iframe => {
          // Reset iframe src to stop video
          const src = iframe.src;
          if (src) {
            iframe.src = src.replace(/[?&](autoplay=1|mute=1|muted=1)/g, '');
          }
        });
        
        // Handle Shopify videos
        const shopifyVideos = card.querySelectorAll('.shopify-video');
        shopifyVideos.forEach(video => {
          video.pause();
          video.currentTime = 0; // Reset to beginning
        });
        
        // Show placeholder again if it exists (for external videos only)
        const placeholders = card.querySelectorAll('.video-placeholder');
        placeholders.forEach(placeholder => {
          placeholder.style.display = 'block';
        });
        
        // Hide iframes
        iframes.forEach(iframe => {
          iframe.style.display = 'none';
        });
      }
    });
  }
  
  // Auto-play functionality
  startAutoPlay() {
    if (!this.autoplayEnabled || this.totalSlides <= 1) return;
    
    this.stopAutoPlay(); // Clear any existing interval
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }
  
  stopAutoPlay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  resetAutoPlay() {
    if (this.autoplayEnabled) {
      this.stopAutoPlay();
      // Restart autoplay after a short delay to allow user interaction
      setTimeout(() => {
        this.startAutoPlay();
      }, 1000);
    }
  }
  
  // Pause auto-play on hover, resume on leave
  setupAutoPlayPause() {
    this.section.addEventListener('mouseenter', () => this.stopAutoPlay());
    this.section.addEventListener('mouseleave', () => {
      if (this.autoplayEnabled) {
        this.startAutoPlay();
      }
    });
    
    // Also pause on focus for accessibility
    this.section.addEventListener('focusin', () => this.stopAutoPlay());
    this.section.addEventListener('focusout', () => {
      if (this.autoplayEnabled) {
        this.startAutoPlay();
      }
    });
  }
}

// Utility function to handle responsive behavior
function handleResponsiveChanges() {
  const sections = document.querySelectorAll('.custom-cards-section');
  
  sections.forEach(section => {
    const navigation = section.querySelector('.cards-navigation');
    const dots = section.querySelector('.cards-dots');
    
    // Show/hide navigation based on screen size and number of cards
    const cards = section.querySelectorAll('.custom-card');
    const shouldShowNavigation = cards.length > 1;
    
    if (navigation) {
      navigation.style.display = shouldShowNavigation ? 'flex' : 'none';
    }
    
    if (dots) {
      dots.style.display = shouldShowNavigation ? 'flex' : 'none';
    }
  });
}

// Initialize slideshows when DOM is ready
function initCustomCardsSlideshow() {
  const sections = document.querySelectorAll('.custom-cards-section');
  
  sections.forEach(section => {
    new CustomCardsSlideshow(section);
  });
  
  // Handle responsive changes
  handleResponsiveChanges();
  window.addEventListener('resize', handleResponsiveChanges);
}

// Initialize on DOM content loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomCardsSlideshow);
} else {
  initCustomCardsSlideshow();
}

// Re-initialize when Shopify theme editor updates the section
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('section-custom-cards')) {
    const section = event.target.querySelector('.custom-cards-section');
    if (section) {
      new CustomCardsSlideshow(section);
    }
  }
});

// Handle theme editor section unload
document.addEventListener('shopify:section:unload', (event) => {
  if (event.target.classList.contains('section-custom-cards')) {
    // Clean up any intervals or event listeners
    const section = event.target.querySelector('.custom-cards-section');
    if (section && section.slideshow) {
      section.slideshow.stopAutoPlay();
    }
  }
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomCardsSlideshow;
}

